import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { StateContext } from '../../../../../../StateProvider';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import Popup from '../../../../../../Utilities/components/Popup';
import { 
  getPaymentMethodById,
  updatePaymentMethod,
  deletePaymentMethod,
  setDefaultPaymentMethod
} from '../../../../../../Utilities/api';
import { WhiteBackButton, BlackDeleteButton } from '../../../../../../Utilities/components/Buttons';
import './EditPaymentMethod.css';
import Footer from '../../../../../../Footer';

const EditPaymentMethod = () => {
  const { id } = useParams();
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const state = useContext(StateContext);

  const user = state ? state.user : null;
  const refreshUserData = state ? state.refreshUserData : function(){};
  const loading = state ? state.loading : false;
  const setLoading = state ? state.setLoading : function(){};

  const [showPopup, setShowPopup] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState(null);

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        padding: '15px'
      },
      invalid: {
        color: '#9e2146'
      }
    }
  };

  /* --------------------------------------------------
     Load payment method
  -------------------------------------------------- */
  useEffect(function () {
    async function fetchData() {
      if (!id) return;

      try {
        const method = await getPaymentMethodById(id);
        setPaymentMethod(method);
        setCardholderName(method.cardholderName || '');

        if (user && user.defaultPaymentMethodId) {
          if (String(user.defaultPaymentMethodId) === String(method.stripePaymentMethodId)) {
            setIsDefault(true);
          } else {
            setIsDefault(false);
          }
        }

      } catch (err) {
        console.error("Failed to fetch PM:", err);
      }
    }

    fetchData();
  }, [id, user ? user.defaultPaymentMethodId : null]);

  /* --------------------------------------------------
     Update Payment Method
  -------------------------------------------------- */
  const handleUpdatePaymentMethod = async function (e) {
    e.preventDefault();
    if (!stripe || !elements || !paymentMethod || !user) return;

    setLoading(true);

    const cardElement = elements.getElement(CardElement);

    try {
      const result = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: { name: cardholderName }
      });

      if (result.error) {
        setError(result.error.message);
        setLoading(false);
        return;
      }

      const newPm = result.paymentMethod;

      await updatePaymentMethod(user.uid, {
        paymentMethodId: paymentMethod.stripePaymentMethodId,
        newPaymentMethodId: newPm.id,
        stripeCustomerId: user.stripeCustomerId,
        cardholderName: cardholderName
      });

      if (isDefault) {
        await setDefaultPaymentMethod(user.uid, newPm.id);
      }

      await refreshUserData(user.uid);
      navigate(-1);

    } catch (err) {
      console.error("Failed to update PM:", err);
      setError("Failed to update payment method");
    }

    setLoading(false);
  };

  /* --------------------------------------------------
     Handle Delete
  -------------------------------------------------- */
  const handlePopupDelete = async function () {
    if (!paymentMethod || !paymentMethod.stripePaymentMethodId) return;

    try {
      await deletePaymentMethod(paymentMethod.stripePaymentMethodId);
      await refreshUserData(user.uid);
      navigate(-1);
    } catch (err) {
      console.error("Failed to delete PM:", err);
    }

    setShowPopup(false);
  };

  /* --------------------------------------------------
     Set Default
  -------------------------------------------------- */
  const handleDefaultChange = async function (e) {
    const newValue = e.target.checked;
    setIsDefault(newValue);

    if (!paymentMethod || !user) return;

    try {
      if (newValue) {
        await setDefaultPaymentMethod(user.uid, paymentMethod.stripePaymentMethodId);
      } else {
        if (
          user.defaultPaymentMethodId &&
          String(user.defaultPaymentMethodId) === String(paymentMethod.stripePaymentMethodId)
        ) {
          await setDefaultPaymentMethod(user.uid, null);
        }
      }
      await refreshUserData(user.uid);
    } catch (err) {
      console.error("Failed to update default PM:", err);
    }
  };

  /* --------------------------------------------------
     Render
  -------------------------------------------------- */

  const userReady = user !== null;
  const pmReady = paymentMethod !== null;

  return (
    <>
      <div className="edit-payment-backdrop">
        <div className="container">

          {/* Popup */}
          <Popup isOpen={showPopup} handleClose={function () { setShowPopup(false); }}>
            <p className="payment-edit-popup-header">Delete Payment Method?</p>
            <div className="payment-edit-popup-button-container">
              <button 
                onClick={function () { setShowPopup(false); }} 
                className="payment-edit-popup-cancel"
              >
                Cancel
              </button>
              <button 
                onClick={handlePopupDelete} 
                className="payment-edit-popup-delete"
              >
                Delete
              </button>
            </div>
          </Popup>

          <div className="payment-edit-button-container">
            <WhiteBackButton />
            <BlackDeleteButton onDelete={function () { setShowPopup(true); }} />
          </div>

          <div className="edit-payment-method">
            <h2 className="edit-payment-header">Update Payment Method</h2>

            {!userReady || !pmReady ? (
              <p>Loading...</p>
            ) : (
              <>
                <div className="edit-payment-info">
                  <p className="edit-payment-info-header">Card Info</p>
                  <hr className="edit-payment-breakline" />
                  <p className="edit-payment-card">
                    Card Number: {paymentMethod.cardNumber}
                  </p>
                  <p className="edit-payment-cardType">
                    Type: {paymentMethod.cardType ? paymentMethod.cardType.toUpperCase() : ""}
                  </p>
                  <p className="edit-payment-expiry">
                    Expires: 
                    {paymentMethod.expiryMonth && paymentMethod.expiryMonth.toString().padStart(2, "0")}/
                    {paymentMethod.expiryYear ? paymentMethod.expiryYear.toString().slice(-2) : ""}
                  </p>
                </div>

                <form>
                  <label htmlFor="cardholderName">Cardholder Name</label>
                  <input
                    id="cardholderName"
                    type="text"
                    value={cardholderName}
                    onChange={function (e) { setCardholderName(e.target.value); }}
                    placeholder="Enter Here"
                  />
                </form>

                <div className="add-payment-stripe-container">
                  <label className="add-payment-stripe-label">Card Details</label>
                  <CardElement className="StripeElement" options={cardElementOptions} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="edit-payment-method-submit-container">
        <div className="default-payment-method">
          <label>
            <input
              type="checkbox"
              checked={isDefault}
              onChange={handleDefaultChange}
              disabled={!pmReady}
            />
            Set as Default
          </label>
        </div>

        <button 
          onClick={handleUpdatePaymentMethod}
          className="edit-payment-method-submit"
          disabled={loading || !pmReady}
        >
          {loading ? "Processing..." : "Update"}
        </button>
      </div>

      <Footer />
    </>
  );
};

export default EditPaymentMethod;
